import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { Icon, type IconName } from "./icons";
import { cx } from "./cx";
import { haptic } from "./useMediaQuery";
import s from "./swiperow.module.css";

export const SWIPE_THRESHOLD = 88;
export const SWIPE_RUBBER = 140;
const LOCK_SLOP = 8;

/** Pure: the displayed offset for a raw drag distance. Linear to 140px, then a third of the extra. */
export function rubberBand(dx: number): number {
  const a = Math.abs(dx);
  const out = a <= SWIPE_RUBBER ? a : SWIPE_RUBBER + (a - SWIPE_RUBBER) / 3;
  return Math.sign(dx) * out;
}

export type SwipeTone = "done" | "snooze" | "read" | "archive";

export interface SwipeAction {
  label: string;
  icon?: IconName;
  tone: SwipeTone;
  onCommit: () => void;
  /** The row stays after the action (mark unread): it springs back instead of leaving. */
  keep?: boolean;
}

export interface SwipeRowProps {
  /** What the row is about ("Karthik Reddy"); screen readers hear "Done: Karthik Reddy". */
  name: string;
  /** Revealed by dragging right. */
  right?: SwipeAction;
  /** Revealed by dragging left. */
  left?: SwipeAction;
  children: ReactNode;
  className?: string;
}

const EVENT = "helmr:swipe-start";

/**
 * Horizontal drag with a left and a right action. Commits on release past 88px; rubber-bands past 140px; a vertical
 * start leaves the gesture to scrolling; one row is dragged at a time. Every action is also a real button (hidden until
 * focused), so touch users can tap the row for a sheet and keyboard and screen-reader users still reach both.
 */
export function SwipeRow({ name, right, left, children, className }: SwipeRowProps) {
  const id = useId();
  const [x, setX] = useState(0);
  const [phase, setPhase] = useState<"idle" | "drag" | "gone">("idle");
  const st = useRef<{ x: number; y: number; axis: "none" | "h" | "v"; armed: boolean } | null>(null);
  const moved = useRef(false);

  useEffect(() => {
    const onOther = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== id) { setX(0); setPhase((p) => (p === "gone" ? p : "idle")); }
    };
    document.addEventListener(EVENT, onOther);
    return () => document.removeEventListener(EVENT, onOther);
  }, [id]);

  const action = x > 0 ? right : x < 0 ? left : undefined;

  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    st.current = { x: e.clientX, y: e.clientY, axis: "none", armed: false };
    moved.current = false;
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const g = st.current;
    if (!g || g.axis === "v") return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (g.axis === "none") {
      if (Math.abs(dx) < LOCK_SLOP && Math.abs(dy) < LOCK_SLOP) return;
      if (Math.abs(dy) > Math.abs(dx)) { g.axis = "v"; return; }
      g.axis = "h";
      document.dispatchEvent(new CustomEvent(EVENT, { detail: id }));
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setPhase("drag");
    }
    if ((dx > 0 && !right) || (dx < 0 && !left)) { setX(0); return; }
    moved.current = true;
    const armed = Math.abs(dx) >= SWIPE_THRESHOLD;
    if (armed !== g.armed) { g.armed = armed; if (armed) haptic("light"); }
    setX(rubberBand(dx));
  };
  const end = (e: PointerEvent<HTMLDivElement>, cancelled = false) => {
    const g = st.current;
    st.current = null;
    if (!g || g.axis !== "h") return;
    const dx = e.clientX - g.x;
    const chosen = dx > 0 ? right : dx < 0 ? left : undefined;
    if (!cancelled && chosen && Math.abs(dx) >= SWIPE_THRESHOLD) {
      haptic("medium");
      if (chosen.keep) { setPhase("idle"); setX(0); } else { setPhase("gone"); setX(dx > 0 ? 480 : -480); }
      chosen.onCommit();
    } else {
      setPhase("idle");
      setX(0);
    }
  };

  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    // Alt+arrow is the keyboard twin of the swipe: right arrow = the right action.
    if (!e.altKey) return;
    if (e.key === "ArrowRight" && right) { e.preventDefault(); right.onCommit(); }
    if (e.key === "ArrowLeft" && left) { e.preventDefault(); left.onCommit(); }
  };

  const reveal = (a: SwipeAction | undefined, side: "r" | "l") => a ? (
    <span className={s.hint} data-side={side}><Icon name={a.icon ?? "check"} size={18} />{a.label}</span>
  ) : <span />;

  return (
    <div className={cx(s.row, className)} data-phase={phase} data-dir={x > 0 ? "r" : x < 0 ? "l" : undefined} data-tone={action?.tone} data-armed={action && Math.abs(x) >= SWIPE_THRESHOLD ? "" : undefined} data-testid="swipe-row">
      <div className={s.under} aria-hidden="true">{reveal(right, "r")}{reveal(left, "l")}</div>
      <div
        className={s.fg}
        style={{ transform: x ? `translateX(${x}px)` : undefined }}
        onPointerDown={down} onPointerMove={move} onPointerUp={(e) => end(e)} onPointerCancel={(e) => end(e, true)}
        onClickCapture={(e) => { if (moved.current) { e.stopPropagation(); e.preventDefault(); moved.current = false; } }}
        onKeyDown={key}
      >
        {children}
      </div>
      <div className={s.acts}>
        {right ? <button type="button" onClick={right.onCommit}>{right.label}: {name}</button> : null}
        {left ? <button type="button" onClick={left.onCommit}>{left.label}: {name}</button> : null}
      </div>
    </div>
  );
}
